import type { ReactNode } from 'react';
import { AlertCircle, AlertTriangle, CheckCircle2, Info, Loader2, WifiOff, type LucideIcon } from 'lucide-react';
import { isApiError } from '@/lib/api/client';
import { errorMessage } from '@/lib/api/query';
import { cn } from '@/lib/cn';
import { Button } from './Button';

/** Loading placeholder with a soft shimmer. Match the shape of the content it replaces. */
export function Skeleton({ className }: { className?: string }) {
  return (
    <div className={cn('relative overflow-hidden rounded-md bg-slate-200/70', className)} aria-hidden>
      <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/60 to-transparent" />
    </div>
  );
}

export function Spinner({ className, label = 'Loading' }: { className?: string; label?: string }) {
  return (
    <span role="status" className="inline-flex">
      <Loader2 className={cn('size-5 animate-spin text-slate-400', className)} aria-hidden />
      <span className="sr-only">{label}</span>
    </span>
  );
}

export interface EmptyStateProps {
  icon?: LucideIcon;
  title: string;
  description?: ReactNode;
  /** The next step, e.g. <Button>Add resident</Button>. Empty states always offer one. */
  action?: ReactNode;
  className?: string;
}

export function EmptyState({ icon: Icon, title, description, action, className }: EmptyStateProps) {
  return (
    <div className={cn('flex flex-col items-center justify-center px-6 py-14 text-center', className)}>
      {Icon && (
        <div className="mb-4 flex size-12 items-center justify-center rounded-full bg-slate-100 text-slate-500">
          <Icon className="size-6" aria-hidden />
        </div>
      )}
      <h3 className="font-display text-base font-semibold text-slate-900">{title}</h3>
      {description && <p className="mt-1 max-w-sm text-sm text-slate-500">{description}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

export interface ErrorStateProps {
  error: unknown;
  onRetry?: () => void;
  title?: string;
  className?: string;
  /** Tighter layout for use inside a card or table. */
  compact?: boolean;
}

/** What users see when a request fails: a plain-language reason and a retry. */
export function ErrorState({ error, onRetry, title, className, compact }: ErrorStateProps) {
  const offline = isApiError(error) && (error.kind === 'network' || error.kind === 'timeout');
  const Icon = offline ? WifiOff : AlertCircle;
  return (
    <div role="alert" className={cn('flex flex-col items-center justify-center text-center', compact ? 'px-4 py-8' : 'px-6 py-14', className)}>
      <div className="mb-3 flex size-12 items-center justify-center rounded-full bg-red-50 text-red-600">
        <Icon className="size-6" aria-hidden />
      </div>
      <h3 className="font-display text-base font-semibold text-slate-900">{title ?? (offline ? "Can't reach the server" : "This didn't load")}</h3>
      <p className="mt-1 max-w-sm text-sm text-slate-500">{errorMessage(error)}</p>
      {onRetry && (
        <Button variant="secondary" size="sm" className="mt-4" onClick={onRetry}>
          Try again
        </Button>
      )}
    </div>
  );
}

export type AlertTone = 'info' | 'success' | 'warning' | 'danger';

const alertTones: Record<AlertTone, { box: string; icon: LucideIcon; iconClass: string }> = {
  info: { box: 'border-sky-200 bg-sky-50 text-sky-900', icon: Info, iconClass: 'text-sky-600' },
  success: { box: 'border-emerald-200 bg-emerald-50 text-emerald-900', icon: CheckCircle2, iconClass: 'text-emerald-600' },
  warning: { box: 'border-amber-200 bg-amber-50 text-amber-900', icon: AlertTriangle, iconClass: 'text-amber-600' },
  danger: { box: 'border-red-200 bg-red-50 text-red-900', icon: AlertCircle, iconClass: 'text-red-600' },
};

export interface AlertProps {
  tone?: AlertTone;
  title?: string;
  children?: ReactNode;
  action?: ReactNode;
  className?: string;
}

/** Inline, persistent message inside a page or form (use toasts only for transient confirmations). */
export function Alert({ tone = 'info', title, children, action, className }: AlertProps) {
  const t = alertTones[tone];
  const Icon = t.icon;
  return (
    <div role={tone === 'danger' ? 'alert' : 'status'} className={cn('flex gap-3 rounded-lg border px-4 py-3 text-sm', t.box, className)}>
      <Icon className={cn('mt-0.5 size-4 shrink-0', t.iconClass)} aria-hidden />
      <div className="min-w-0 flex-1">
        {title && <p className="font-semibold">{title}</p>}
        {children && <div className={cn(title && 'mt-0.5', 'text-[0.8125rem] leading-relaxed opacity-90')}>{children}</div>}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}
